import React, { useState, useEffect, useCallback } from 'react';
import {
  getLedgerSummary,
  getPropertyLedger,
  runReconciliation,
  runCategorization,
  LedgerSummary,
  PropertyLedger as PropertyLedgerData,
  LineItem,
} from '../services/ledger.service';
import { api } from '../config/api';
import { tw } from '../utils/terminalTheme';
import {
  FiHome, FiFileText, FiTool, FiRefreshCw, FiTag,
  FiPaperclip, FiChevronDown, FiChevronRight,
} from 'react-icons/fi';

const monoFont = "'JetBrains Mono', 'Fira Code', monospace";
const fmt = (n: number) => '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2 });

// Reconciliation status -> color + label
const STATUS_META: Record<string, { label: string; cls: string; dot: string }> = {
  bank_confirmed: { label: 'Paid (bank-confirmed)', cls: 'text-green-400', dot: 'bg-green-400' },
  possible:       { label: 'Possible (review)',     cls: 'text-amber-400', dot: 'bg-amber-400' },
  pending:        { label: 'Pending (no match)',    cls: 'text-gray-400',  dot: 'bg-gray-500' },
  quote:          { label: 'Quote (likely unpaid)', cls: 'text-red-400',   dot: 'bg-red-400' },
};

export const PropertyLedger: React.FC = () => {
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [detail, setDetail] = useState<Record<number, PropertyLedgerData>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await getLedgerSummary());
    } catch (e) {
      console.error('ledger summary failed', e);
      setMsg('Failed to load ledger.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doReconcile = async () => {
    setBusy('reconcile'); setMsg(null);
    try {
      const r = await runReconciliation();
      const s = r.summary || {};
      setMsg(`Reconciled: ${s.matched} matched (${s.high} confirmed, ${s.low} to review), ${s.unmatched} unmatched.`);
      await load();
    } catch (e) {
      setMsg('Reconciliation failed.');
    } finally { setBusy(null); }
  };

  const doCategorize = async () => {
    setBusy('categorize'); setMsg(null);
    try {
      const r = await runCategorization(false);
      const rs = r.rule_summary || {};
      setMsg(`Categorized ${rs.rule_matched} expenses by rule (${rs.uncategorized_before} were uncategorized).`);
      await load();
    } catch (e) {
      setMsg('Categorization failed.');
    } finally { setBusy(null); }
  };

  const toggle = async (pid: number) => {
    if (expanded === pid) { setExpanded(null); return; }
    setExpanded(pid);
    if (!detail[pid]) {
      try {
        const d = await getPropertyLedger(pid);
        setDetail(prev => ({ ...prev, [pid]: d }));
      } catch (e) { console.error('property ledger failed', e); }
    }
  };

  const viewReceipt = (documentId: number | null) => {
    if (!documentId) return;
    const token = localStorage.getItem('accessToken');
    window.open(`${api.defaults.baseURL}/documents/${documentId}/preview?token=${token}`, '_blank');
  };

  const p = summary?.portfolio;
  const props = summary?.properties || [];

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: monoFont }}>
            <span className="text-cyan-400">~/</span>ledger
            <span className="text-green-500 text-base ml-2">$</span>
            <span className="text-gray-500 text-base ml-1">reconcile --by-property</span>
          </h1>
          <p className="text-gray-500 text-sm mt-1" style={{ fontFamily: monoFont }}>
            Per-property spend reconciled against the bank — Paid / Possible / Pending / Quote, with a repairs cut
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={doReconcile} disabled={!!busy} className={tw.btnSecondary + ' flex items-center gap-2 disabled:opacity-50'}>
            <FiRefreshCw className={busy === 'reconcile' ? 'animate-spin' : ''} /> Run Reconciliation
          </button>
          <button onClick={doCategorize} disabled={!!busy} className={tw.btnSecondary + ' flex items-center gap-2 disabled:opacity-50'}>
            <FiTag className={busy === 'categorize' ? 'animate-spin' : ''} /> Categorize
          </button>
        </div>
      </div>

      {msg && (
        <div className="mb-4 px-4 py-2 rounded-md bg-cyan-600/10 border border-cyan-500/30 text-cyan-300 text-sm font-mono">
          {msg}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Total Recorded</div>
          <div className="text-cyan-400 text-xl font-bold font-mono">{fmt(p?.totals.total_recorded || 0)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Bank-Confirmed</div>
          <div className="text-green-400 text-xl font-bold font-mono">{fmt(p?.totals.bank_confirmed || 0)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Possible (review)</div>
          <div className="text-amber-400 text-xl font-bold font-mono">{fmt(p?.totals.possible || 0)}</div>
        </div>
        <div className={tw.statCard}>
          <div className={tw.statLabel}>Pending</div>
          <div className="text-gray-300 text-xl font-bold font-mono">{fmt(p?.totals.pending || 0)}</div>
        </div>
        <div className={tw.statCard + ' border-orange-500/30'}>
          <div className={tw.statLabel + ' text-orange-400/70'}>Repairs (total / conf.)</div>
          <div className="text-orange-400 text-xl font-bold font-mono">{fmt(p?.repairs.total || 0)}</div>
          <div className="text-orange-300/60 text-xs font-mono">{fmt(p?.repairs.confirmed || 0)} confirmed</div>
        </div>
      </div>

      {loading && <div className="text-center text-gray-500 py-12 font-mono">Loading ledger…</div>}

      {!loading && summary && (
        <div className="space-y-6">
          {/* Category breakdown */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 px-4 pt-4 mb-2">
              <FiFileText className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Spend by Category</h2>
              <span className="text-gray-600 text-xs font-mono ml-auto">total recorded · confirmed</span>
            </div>
            <div className="px-4 pb-4 space-y-1">
              {p?.categories.map(c => {
                const pct = p.totals.total_recorded ? (c.amount / p.totals.total_recorded) * 100 : 0;
                const isRepairs = c.category === 'Repairs & Maintenance';
                return (
                  <div key={c.category} className="flex items-center gap-3 text-xs font-mono py-1">
                    <div className={`w-44 truncate ${isRepairs ? 'text-orange-400 font-semibold' : 'text-gray-300'}`}>
                      {isRepairs && <FiTool className="inline mr-1" />}{c.category}
                    </div>
                    <div className="flex-1 h-2 bg-gray-800 rounded overflow-hidden">
                      <div className={`h-full ${isRepairs ? 'bg-orange-500/70' : 'bg-cyan-500/50'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-24 text-right text-gray-200">{fmt(c.amount)}</div>
                    <div className="w-24 text-right text-green-400/80">{fmt(c.confirmed)}</div>
                    <div className="w-10 text-right text-gray-600">{c.count}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Per-property table */}
          <div className={tw.tableWrap}>
            <div className="flex items-center gap-2 px-4 pt-4 mb-2">
              <FiHome className="text-cyan-400" />
              <h2 className="text-white font-semibold font-mono">Properties</h2>
              <span className="text-gray-600 text-xs font-mono ml-auto">{props.length} properties · click to drill into receipts</span>
            </div>
            <div className="px-4 pb-4">
              <div className="grid grid-cols-12 gap-2 text-xs font-mono text-gray-500 border-b border-gray-700/30 pb-2 mb-1">
                <div className="col-span-4">Property</div>
                <div className="col-span-2 text-right">Recorded</div>
                <div className="col-span-2 text-right text-green-500/70">Confirmed</div>
                <div className="col-span-2 text-right text-amber-500/70">Possible</div>
                <div className="col-span-2 text-right text-orange-500/70">Repairs</div>
              </div>
              {props.map(row => (
                <div key={row.property_id}>
                  <div
                    onClick={() => toggle(row.property_id)}
                    className="grid grid-cols-12 gap-2 text-xs font-mono py-2 border-b border-gray-800/30 hover:bg-gray-800/20 cursor-pointer transition-colors"
                  >
                    <div className="col-span-4 flex items-center gap-1 text-gray-200">
                      {expanded === row.property_id ? <FiChevronDown /> : <FiChevronRight />}
                      <span className="truncate">{row.property_name}</span>
                    </div>
                    <div className="col-span-2 text-right text-cyan-300">{fmt(row.totals.total_recorded)}</div>
                    <div className="col-span-2 text-right text-green-400">{fmt(row.totals.bank_confirmed)}</div>
                    <div className="col-span-2 text-right text-amber-400">{fmt(row.totals.possible)}</div>
                    <div className="col-span-2 text-right text-orange-400">{fmt(row.repairs.total)}</div>
                  </div>

                  {/* Drill-down: line items */}
                  {expanded === row.property_id && (
                    <div className="bg-gray-900/40 border-b border-gray-800/30 px-3 py-2">
                      {!detail[row.property_id] ? (
                        <div className="text-gray-500 text-xs font-mono py-3 text-center">Loading receipts…</div>
                      ) : (
                        <LineItemTable items={detail[row.property_id].line_items} onView={viewReceipt} />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs font-mono text-gray-500">
            {Object.entries(STATUS_META).map(([k, m]) => (
              <span key={k} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-sm ${m.dot}`} /> {m.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LineItemTable: React.FC<{ items: LineItem[]; onView: (id: number | null) => void }> = ({ items, onView }) => {
  if (!items.length) return <div className="text-gray-500 text-xs font-mono py-3 text-center">No expenses</div>;
  return (
    <div className="space-y-0.5">
      {items.map(li => {
        const m = STATUS_META[li.status];
        return (
          <div key={li.id} className="grid grid-cols-12 gap-2 text-xs font-mono py-1 items-center hover:bg-gray-800/30 rounded px-1">
            <div className="col-span-2 text-gray-500">{(li.date || '').slice(0, 10)}</div>
            <div className="col-span-3 text-gray-300 truncate">{li.vendor || '—'}</div>
            <div className="col-span-2 truncate text-gray-500">{li.category || 'Uncategorized'}</div>
            <div className="col-span-2 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-sm ${m.dot}`} />
              <span className={m.cls}>{li.status === 'bank_confirmed' ? 'Paid' : li.status === 'possible' ? 'Review' : li.status === 'quote' ? 'Quote' : 'Pending'}</span>
            </div>
            <div className="col-span-2 text-right text-gray-200">{fmt(li.amount)}</div>
            <div className="col-span-1 text-right">
              {li.document_id && (
                <button onClick={() => onView(li.document_id)} className="text-gray-500 hover:text-cyan-400" title="View receipt">
                  <FiPaperclip />
                </button>
              )}
            </div>
            {li.bank_payee && (
              <div className="col-span-12 text-[10px] text-gray-600 pl-2 -mt-0.5">
                ↳ bank {(li.bank_date || '').slice(0, 10)} · {li.bank_payee}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
